'use strict';

Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });

const tslib_es6 = require('../../../tslib.es6-CZ43up4V.js');
const React = require('react');
const react = require('@emotion/react');
const components_display_Tooltip_Tooltip = require('../../display/Tooltip/Tooltip.cjs.js');
const utils_createUniqueCustomProperty = require('../../../utils/createUniqueCustomProperty.cjs.js');
const theme_Animation = require('../../../theme/Animation.cjs.js');
require('../../../theme/Colors.cjs.js');
require('../../../theme/Shadows.cjs.js');
const theme_Typography = require('../../../theme/Typography.cjs.js');
const styleEncapsulation = require('@zapier/style-encapsulation');
const utils_uniqueId = require('../../../utils/uniqueId.cjs.js');
const styleMixins_reset = require('../../../style-mixins/reset.cjs.js');

const Vars = utils_createUniqueCustomProperty.createUniqueCustomProperties("Input", [
  "borderRadius",
  "iconColor",
  "inputValueColor",
  "inputHeight",
  "inputPaddingLeft",
  "inputPaddingRight",
  "inputPaddingTop",
  "inputPaddingBottom",
  "prependPaddingLeft",
  "appendPaddingRight",
  "outlineColor",
  "placeholderColor",
  "prependAppendColor",
  "tooltipOpacity"
]);
const Styles = styleEncapsulation.labelStyles("Input", {
  root: react.css`
    ${Vars.borderRadius}: var(--zds-radius-medium);
    ${Vars.iconColor}: var(--zds-text-weaker);
    ${Vars.inputHeight}: var(--zds-size-medium);
    ${Vars.inputPaddingLeft}: 15px;
    ${Vars.inputPaddingRight}: 15px;
    ${Vars.inputPaddingTop}: 9px;
    ${Vars.inputPaddingBottom}: 9px;
    ${Vars.placeholderColor}: var(--zds-text-weakest);
    ${Vars.inputValueColor}: var(--zds-text-default);
    ${Vars.prependPaddingLeft}: 15px;
    ${Vars.appendPaddingRight}: 15px;
    ${Vars.outlineColor}: var(--zds-stroke-default);
    ${Vars.prependAppendColor}: var(--zds-text-stronger);
    ${Vars.tooltipOpacity}: 0;
    ${styleMixins_reset.boxSizing};
    border: 1px solid var(${Vars.outlineColor});
    // Box-shadow only for focus state
    box-shadow: none;
    // For Tooltip positioning
    position: relative;
    border-radius: var(${Vars.borderRadius});
    background-color: var(--zds-background-weaker);

    // Only transition specific properties because some,
    // like 'padding', will change but shouldn't transition.
    transition-duration: ${theme_Animation.Animation.transitionDuration};
    transition-property: background-color, color, box-shadow;
    transition-timing-function: ease;

    &[data-errored] {
      ${Vars.outlineColor}: var(--zds-status-error);
    }

    &:not([data-disabled]):hover {
      ${Vars.outlineColor}: var(--zds-stroke-stronger);
    }

    &:not([data-disabled]):focus-within {
      ${Vars.outlineColor}: var(--zds-stroke-stronger);
      box-shadow: 0 0 0 var(--zds-radius-none) var(${Vars.outlineColor});

      outline: var(--zds-border-focus);
      outline-offset: -2px;
    }

    &[data-disabled] {
      ${Vars.prependAppendColor}: var(--zds-text-default);
      ${Vars.outlineColor}: var(--zds-stroke-weaker);
      ${Vars.iconColor}: var(--zds-text-weaker);
      ${Vars.inputValueColor}: var(--zds-text-default);
      ${Vars.placeholderColor}: var(--zds-text-weakest);
      cursor: not-allowed;
      background-color: var(--zds-background-strongest);
    }

    &[data-readonly]:hover,
    &[data-disabled]:hover {
      ${Vars.tooltipOpacity}: 1;
    }

    &[data-full-width] {
      width: 100%;
      max-width: 100%;
    }

    &[data-icon-before] {
      ${Vars.prependPaddingLeft}: 0;
      ${Vars.inputPaddingLeft}: 0;
    }

    &[data-prepend][data-layout='inline'] {
      ${Vars.inputPaddingLeft}: 0;
    }

    &[data-icon-after] {
      ${Vars.appendPaddingRight}: 0;
      ${Vars.inputPaddingRight}: 0;
    }

    &[data-append][data-layout='inline'] {
      ${Vars.inputPaddingRight}: 0;
    }

    &[data-size='large'] {
      ${Vars.inputHeight}: var(--zds-size-large);
    }

    &[data-radius='none'] {
      ${Vars.borderRadius}: var(--zds-radius-none);
    }

    &[data-radius='small'] {
      ${Vars.borderRadius}: var(--zds-radius-small);
    }

    &[data-radius='large'] {
      ${Vars.borderRadius}: var(--zds-radius-large);
    }
  `,
  inputLayout: react.css`
    ${styleMixins_reset.boxSizing};
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  `,
  prependAppend: react.css`
    ${styleMixins_reset.boxSizing};
    color: var(${Vars.prependAppendColor});
    border-color: var(${Vars.outlineColor});

    &[data-layout='inline'] {
      ${theme_Typography.Typography.Body2}
      border: none;

      &[data-type='prepend'] {
        margin-left: var(${Vars.prependPaddingLeft});
        margin-right: 10px;
      }

      &[data-type='append'] {
        margin-left: 10px;
        margin-right: var(${Vars.appendPaddingRight});
      }
    }

    &[data-layout='block'] {
      ${theme_Typography.Typography.SmallPrint1}
      background-color: var(--zds-background-stronger);
      display: flex;
      align-items: center;
      width: 100%;
      height: 30px;
      padding: 0 var(${Vars.inputPaddingLeft});

      &[data-type='prepend'] {
        // Ensure prepend renders before icon.
        order: -1;
        border-top-left-radius: var(${Vars.borderRadius});
        border-top-right-radius: var(${Vars.borderRadius});
      }

      &[data-type='append'] {
        // Ensure append renders after icon.
        // The value is a contrived number that needs to be
        // greater than the number of siblings it has.
        order: calc(infinity);
        border-bottom-left-radius: var(${Vars.borderRadius});
        border-bottom-right-radius: var(${Vars.borderRadius});
      }
    }
  `,
  input: react.css`
    all: unset;
    display: revert;
    box-sizing: border-box;
    flex: 1;
    ${theme_Typography.Typography.Body2}
    color: var(${Vars.inputValueColor});
    height: 100%;
    padding-top: var(${Vars.inputPaddingTop});
    padding-bottom: var(${Vars.inputPaddingBottom});
    padding-left: var(${Vars.inputPaddingLeft});
    padding-right: var(${Vars.inputPaddingRight});

    &:not(textarea) {
      height: var(${Vars.inputHeight});
    }

    &:is(textarea) {
      white-space: revert;
    }

    // Style placeholder. Selectors cannot be combined, so keep 'em separated.
    &::placeholder {
      color: var(${Vars.placeholderColor});
      // Necessary for Safari.
      -webkit-text-fill-color: var(currentColor);
    }
    &::-moz-placeholder {
      color: var(${Vars.placeholderColor});
      // Firefox has <1 'opacity' on its placeholders,
      // so it has to be overridden.
      opacity: 1;
    }

    &[data-size='compact'] {
      &::placeholder {
        ${theme_Typography.Typography.MinimalPrint2}
      }
    }

    // Bump font size for devices that don't support hovering
    // to prevent mobile browsers from zooming in on inputs
    // when they're focused. 16px is defined at the OS level
    // of these devices, specifically iOS.
    @media (hover: none) {
      font-size: 16px;
    }

    // Hide "clear selection" X icon in Edge.
    // https://zappy.zapier.com/F693DF53-79B7-4857-AADE-73E9BF3011DB.png
    &::-ms-clear {
      display: none;
    }
    // Hide "clear selection" X icon in Safari.
    &::-webkit-search-cancel-button {
      display: none;
    }

    &[disabled] {
      cursor: not-allowed;
      color: var(--zds-text-weakest);
      // Safari-specific: needed to ensure consistent text color in disabled state.
      -webkit-text-fill-color: currentColor;
      white-space: pre-wrap;
      ${Vars.placeholderColor}: var(--zds-text-weaker);
    }
  `,
  icon: react.css`
    ${styleMixins_reset.boxSizing};
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(${Vars.iconColor});
    position: relative;
    z-index: 1;
    pointer-events: none;
    margin: 0 8px;
    height: var(${Vars.inputHeight});

    &[data-interactive] {
      pointer-events: auto;
    }

    &:not([data-size='compact']) [data-testid='iconContainer'] {
      width: var(--zds-size-small);
      height: var(--zds-size-small);
    }
  `,
  tooltip: react.css`
    ${styleMixins_reset.boxSizing};
    opacity: var(${Vars.tooltipOpacity});
    transition: ${theme_Animation.Animation.transitionValue};
    position: absolute;
    left: 50%;
    transform: translate(0, -15px);
    z-index: 2;
  `
});
const Input = (_a) => {
  var { canClickIconBefore = true, canClickIconAfter = true, disabledText = "Disabled", readOnlyText = "Read-Only", isFocusable = true, layout = "inline", isFullWidth = false, size = "medium", borderRadius = "medium" } = _a, otherProps = tslib_es6.__rest(_a, ["canClickIconBefore", "canClickIconAfter", "disabledText", "readOnlyText", "isFocusable", "layout", "isFullWidth", "size", "borderRadius"]);
  const props = Object.assign({
    canClickIconBefore,
    canClickIconAfter,
    disabledText,
    readOnlyText,
    isFocusable,
    layout,
    isFullWidth,
    size,
    borderRadius
  }, otherProps);
  const [isFocused, setIsFocused] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [tooltipId] = React.useState(props.id ? `${props.id}-tooltip` : utils_uniqueId.uniqueId(`Input__tooltip-`));
  const shouldRenderTooltip = props.isDisabled && !!props.disabledText || props.isReadonly && !!props.readOnlyText;
  const renderIconProps = {
    iconSize: props.size === "compact" ? 20 : 24,
    isFocused,
    isHovered
  };
  const iconBefore = props.renderIconBefore && props.renderIconBefore(renderIconProps);
  const iconAfter = props.renderIconAfter && props.renderIconAfter(renderIconProps);
  const derivedState = {
    hasIconBefore: !!iconBefore,
    hasIconAfter: !!iconAfter,
    isFocused,
    isHovered
  };
  return react.jsx(
    "div",
    { css: Styles.root, "data-append": props.appendContent ? "" : void 0, "data-disabled": props.isDisabled ? "" : void 0, "data-readonly": props.isReadonly ? "" : void 0, "data-errored": props.isErrored ? "" : void 0, "data-full-width": props.isFullWidth ? "" : void 0, "data-icon-after": iconAfter ? "" : void 0, "data-icon-before": iconBefore ? "" : void 0, "data-layout": props.layout, "data-prepend": props.prependContent ? "" : void 0, "data-radius": props.borderRadius, "data-size": props.size, "data-zds": true, onBlur: () => setIsFocused(false), onClick: props.onClick, onFocus: () => setIsFocused(true), onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), role: "presentation" },
    react.jsx(
      "div",
      { css: Styles.inputLayout, "data-zds": true },
      iconBefore && react.jsx("div", { css: Styles.icon, "data-interactive": props.canClickIconBefore ? "" : void 0, "data-size": props.size, "data-zds": true }, iconBefore),
      props.prependContent && react.jsx("div", { css: Styles.prependAppend, "data-layout": props.layout, "data-size": props.size, "data-type": "prepend", "data-zds": true }, props.prependContent),
      props.children({
        "aria-activedescendant": props["aria-activedescendant"],
        "aria-controls": props["aria-controls"],
        "aria-describedby": shouldRenderTooltip ? tooltipId : props["aria-describedby"],
        "aria-errormessage": props.isErrored ? props["aria-describedby"] : void 0,
        "aria-invalid": props.isErrored,
        "aria-label": props["aria-label"],
        "aria-labelledby": props["aria-labelledby"],
        "data-size": props.size,
        autoComplete: props.autoComplete,
        autoFocus: props.autoFocus,
        css: Styles.input,
        tabIndex: props.isFocusable ? void 0 : -1,
        disabled: props.isDisabled,
        id: props.id,
        maxLength: props.maxLength,
        name: props.name,
        onBlur: props.onBlur,
        onChange: props.onChange,
        onFocus: props.onFocus,
        onKeyDown: props.onKeyDown,
        placeholder: props.placeholder,
        readOnly: props.isReadonly,
        required: props.isRequired,
        role: props.role,
        value: props.value
      }, derivedState),
      props.appendContent && react.jsx("div", { css: Styles.prependAppend, "data-layout": props.layout, "data-type": "append", "data-size": props.size, "data-zds": true }, props.appendContent),
      iconAfter && react.jsx("div", { css: Styles.icon, "data-interactive": props.canClickIconAfter ? "" : void 0, "data-size": props.size, "data-zds": true }, iconAfter)
    ),
    shouldRenderTooltip && react.jsx(
      "div",
      { css: Styles.tooltip, "data-zds": true },
      react.jsx(components_display_Tooltip_Tooltip.Tooltip, { id: tooltipId, position: "south" }, props.isReadonly ? props.readOnlyText : props.disabledText)
    )
  );
};

exports.Input = Input;
//# sourceMappingURL=Input.cjs.js.map
